// RS Cart + Favourites Drawer
// Cart half: still owned here. Favourites half: delegates to window.rsFavourites
// (Swym Wishlist Plus wrapper in assets/rs-favourites.js). All wishlist
// persistence and state lives in Swym now — this file only orchestrates UI.

(function () {
  'use strict';

  /**
   * Qualify a URL with origin if relative. Shopify's image_url filter
   * returns paths like "/cdn/shop/files/...png?v=...&width=400" which
   * Swym's na validator rejects on the addToWishList path - it requires
   * a fully-qualified URL (used for the cross-domain wishlist popup +
   * email rendering). Same treatment for product URLs.
   */
  function qualifyUrl(raw) {
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.indexOf('//') === 0) return window.location.protocol + raw;
    if (raw.indexOf('/') === 0)  return window.location.origin + raw;
    return raw;
  }

  /**
   * Build a Swym SwymPageData object from a .rs-heart-btn element's data-*.
   * Returns null if the required IDs are missing (e.g., legacy markup
   * served stale from CDN cache during the migration window).
   *
   * Field mapping:
   *   empi <- data-product-id  (numeric Shopify product ID)
   *   epi  <- data-variant-id  (numeric Shopify variant ID — first available)
   *   du   <- absolute URL from data-url (Swym requires fully-qualified)
   *   dt   <- data-title
   *   iu   <- data-image (qualified to absolute via qualifyUrl)
   *   pr   <- parsed numeric price from data-price string
   */
  function swymDataFromHeart(btn) {
    var empi = parseInt(btn.dataset.productId, 10);
    var epi  = parseInt(btn.dataset.variantId, 10);
    if (!empi || !epi) return null;
    var rawPrice = (btn.dataset.price || '').replace(/[^0-9.]/g, '');
    var pr = rawPrice ? parseFloat(rawPrice) : 0;
    return {
      empi: empi,
      epi:  epi,
      du:   qualifyUrl(btn.dataset.url || ''),
      dt:   btn.dataset.title || '',
      iu:   qualifyUrl(btn.dataset.image || ''),
      pr:   pr
    };
  }

  /**
   * Money formatter (drawer subtotal, line items, favourites). Rule: drop
   * trailing zeros on whole dollars ($650), keep real cents ($1,150.50);
   * thousands-separated, no currency code. The drawer's Subtotal and Total
   * carry the small "CAD" from Liquid instead: sections/cart-popup.liquid puts
   * snippets/rs-price-cur.liquid AFTER #rs-cart-subtotal / #rs-cart-total, so
   * writing textContent into those ids never touches it (2026-10-17). Line
   * prices stay plain on purpose.
   * rsFormatDollars takes a DOLLAR amount; rsMoney takes integer CENTS.
   */
  function rsFormatDollars(n) {
    var s = Number.isInteger(n)
      ? n.toLocaleString('en-CA')
      : n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '$' + s;
  }
  function rsMoney(cents) { return rsFormatDollars(cents / 100); }

  /**
   * Escape text from /cart.js before it goes into a string-built innerHTML
   * (product, variant and discount titles). A discount title can come from a
   * /discount/CODE link, so it is never trusted as markup.
   */
  function rsEsc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Dawn's assets/icon-discount.svg (the tag /cart shows beside a discount),
  // inlined because the drawer is built in JS. Decorative: the text names it.
  var RS_DISCOUNT_ICON =
    '<svg class="rs-cart-discount-icon" viewBox="0 0 12 12" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"/>' +
    '</svg>';

  /**
   * Format a Swym item's pr field as a display price string. Swym's Shopify
   * adapter returns dollars float; we coerce via the same whole-vs-cents rule.
   * Strings pass through unchanged so a server-side pre-formatted value stays
   * formatted.
   */
  function formatPrice(pr) {
    if (typeof pr === 'string') return pr;
    if (typeof pr !== 'number' || isNaN(pr)) return '';
    return rsFormatDollars(pr);
  }

  /**
   * Toggle wishlist membership for a product. Membership is PRODUCT-LEVEL
   * (Option A): one entry per product, colourway not preserved. Both the fill
   * paint (rsUpdateHeartButtons) and this toggle read the SAME identity —
   * hasProduct(empi) — so a filled heart always toggles to remove. Storage
   * rows may still carry a variant id (add passes whatever variant the heart
   * emits, and that's fine), but has(empi, epi) is NOT the membership test for
   * hearts: a variant-level read here was the duplicate-on-un-favourite bug
   * (heart painted filled product-level while the toggle missed on the exact
   * variant). remove() clears every row for the empi.
   */
  window.rsToggleFavourite = function (heartBtn) {
    if (!window.rsFavourites) return;
    var data = swymDataFromHeart(heartBtn);
    if (!data) return;

    var alreadySaved = rsFavourites.hasProduct(data.empi);
    var op = alreadySaved ? rsFavourites.remove(data) : rsFavourites.add(data);

    op.then(function () {
      // Toast only on the add path — onChange will repaint hearts + badge.
      if (!alreadySaved) {
        var toast = document.getElementById('rs-product-saved-toast');
        if (toast) {
          toast.classList.add('rs-saved-toast--visible');
          setTimeout(function () { toast.classList.remove('rs-saved-toast--visible'); }, 2000);
        }
      }
    }).catch(function (err) {
      // Phase 3 fifth-pass: wrapper now rejects on server-truth refutation
      // (server didn't persist), not on flaky SDK callback err. Kept at
      // debug level so the b.apply false-positive era doesn't carry a
      // residual alarm volume — genuine failures still land here but the
      // wrapper's own optimistic-rollback already repainted the heart.
      console.debug('rsToggleFavourite: Swym op (advisory)', err);
    });
  };

  // ── Badge (cart bag = cart count; header heart = favourites count) ─────────
  window.rsUpdateBadge = function () {
    var headerBadge = document.getElementById('rs-badge');
    if (headerBadge) {
      var cartCount = parseInt(headerBadge.dataset.cartCount || 0, 10);
      headerBadge.textContent = cartCount > 0 ? cartCount : '';
      headerBadge.style.display = cartCount > 0 ? '' : 'none';
    }

    var favCount = (window.rsFavourites ? rsFavourites.count() : 0);

    // Dedicated header heart badge (Commit 6).
    var favHeaderBadge = document.getElementById('rs-fav-badge');
    if (favHeaderBadge) {
      favHeaderBadge.textContent = favCount > 0 ? favCount : '';
      favHeaderBadge.style.display = favCount > 0 ? '' : 'none';
    }

    // Drawer tab "(N)" counter — different element id (#rs-badge-fav).
    var favTabBadge = document.getElementById('rs-badge-fav');
    if (favTabBadge) {
      favTabBadge.textContent = favCount > 0 ? '(' + favCount + ')' : '';
    }
  };

  // ── Heart button state sync ─────────────────────────────────────────────────
  // Product-level fill (Option A): any row for the product's empi fills the
  // heart. This is the SAME identity the toggle uses (hasProduct) — the two
  // are deliberately consistent, so a filled heart always toggles to remove.
  // has(empi, epi) is not the membership test. Avoids the "wait, am I only
  // viewing the Ivory variant?" confusion at card surfaces where variant
  // context is ambient.
  window.rsUpdateHeartButtons = function () {
    if (!window.rsFavourites) return;
    document.querySelectorAll('.rs-heart-btn').forEach(function (btn) {
      var empi = parseInt(btn.dataset.productId, 10);
      var faved = empi ? rsFavourites.hasProduct(empi) : false;
      btn.classList.toggle('rs-heart-btn--active', faved);
      btn.setAttribute('aria-label', faved ? 'Remove from favourites' : 'Save to favourites');
    });
  };

  function initHeartButtons() {
    document.querySelectorAll('.rs-heart-btn').forEach(function (btn) {
      if (btn.dataset.rsInited) return;
      btn.dataset.rsInited = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        rsToggleFavourite(btn);
      });
    });
    rsUpdateHeartButtons();
  }

  // Watch for dynamically added cards (search results, etc.)
  // Debounced so that rsUpdateHeartButtons' own DOM writes don't re-trigger it.
  var _heartObserverTimer = null;
  var _heartObserver = new MutationObserver(function () {
    clearTimeout(_heartObserverTimer);
    _heartObserverTimer = setTimeout(initHeartButtons, 150);
  });

  // ── Drawer open / close ─────────────────────────────────────────────────────
  // Element that held focus before the drawer opened, so close() can return
  // focus there. inert (set on close) blurs whatever was focused inside the
  // drawer; without this the keyboard user would be dropped to <body>.
  var rsDrawerReturnFocus = null;

  window.rsOpenDrawer = function (tab) {
    var drawer  = document.getElementById('rs-drawer');
    var overlay = document.getElementById('rs-drawer-overlay');
    if (!drawer) return;
    rsDrawerReturnFocus = document.activeElement;
    drawer.classList.add('rs-drawer--open');
    drawer.removeAttribute('inert');
    if (overlay) overlay.classList.add('rs-drawer-overlay--visible');
    document.body.style.overflow = 'hidden';
    var showCart = (tab || 'cart') === 'cart';
    rsShowTab(tab || 'cart', true);
    // Digital #97: the shared cart read (assets/rs-cart-events.js) — right
    // after an add it reuses the request the add already made. Falls back to
    // a plain fetch if that file did not load.
    var ce = window.rsCartEvents;
    (ce ? ce.fetchCart() : fetch('/cart.js').then(function (r) { return r.json(); }))
      .then(function (cart) {
        if (!cart) return;
        rsRenderCart(cart);
        // Shopify standard event: the cart is on screen. Only when the drawer
        // opens on the Cart tab; opened on Favourites (heart) it is not
        // (QA F2 round 2). A later switch to Cart sends it (rsShowTab).
        if (ce && showCart) ce.cartView(drawer, 'dialog', cart);
        rsRenderFavourites();
        rsUpdateBadge();
        // Drawer gift panel (Digital #96): it was seeded from cart.note at page
        // load; the note may have changed since (edited on /cart, another tab).
        // reseed() skips itself while the shopper is editing the box.
        var giftPanel = document.querySelector('#rs-pane-cart rs-gift-panel');
        if (giftPanel && typeof giftPanel.reseed === 'function') giftPanel.reseed(cart.note);
      })
      .catch(function () {});
  };

  window.rsCloseDrawer = function () {
    var drawer  = document.getElementById('rs-drawer');
    var overlay = document.getElementById('rs-drawer-overlay');
    if (!drawer) return;
    drawer.classList.remove('rs-drawer--open');
    drawer.setAttribute('inert', '');
    if (overlay) overlay.classList.remove('rs-drawer-overlay--visible');
    document.body.style.overflow = '';
    // Return focus to whatever opened the drawer, if it's still around and
    // focusable. No fallback — if the opener is gone, leave focus alone.
    if (rsDrawerReturnFocus &&
        rsDrawerReturnFocus !== document.body &&
        typeof rsDrawerReturnFocus.focus === 'function' &&
        document.contains(rsDrawerReturnFocus)) {
      rsDrawerReturnFocus.focus();
    }
    rsDrawerReturnFocus = null;
  };

  // Legacy aliases — keep any existing code calling the old names working
  window.openCartPopup  = window.rsOpenDrawer;
  window.closeCartPopup = window.rsCloseDrawer;

  // ── Publish seam ────────────────────────────────────────────────────────────
  // Lets non-pubsub surfaces (e.g. rs-quick-add.js, a plain IIFE without the
  // pubsub globals in scope) emit cartUpdate so the badge-repaint subscriber
  // below keeps the header bag count in sync after in-page cart mutations.
  window.rsPublishCartUpdate = function (detail) {
    publish(PUB_SUB_EVENTS.cartUpdate, detail);
  };

  // ── Tab switching ───────────────────────────────────────────────────────────
  // fromOpen: called by rsOpenDrawer, which sends its own cart:view.
  window.rsShowTab = function (tab, fromOpen) {
    var cartPane = document.getElementById('rs-pane-cart');
    var cartWasShown = !!(cartPane && !cartPane.classList.contains('rs-drawer__pane--hidden'));
    ['cart', 'fav'].forEach(function (t) {
      var pane   = document.getElementById('rs-pane-' + t);
      var tabBtn = document.getElementById('rs-tab-' + t);
      if (!pane || !tabBtn) return;
      var active = (t === tab);
      pane.classList.toggle('rs-drawer__pane--hidden', !active);
      tabBtn.classList.toggle('rs-drawer__tab--active', active);
    });
    // Digital #97 (QA F2 round 2): Favourites → Cart inside the open drawer
    // puts the cart on screen: Shopify standard cart:view ('dialog'), from the
    // shared read (no request when one is under 2 s old and still fresh).
    var drawer = document.getElementById('rs-drawer');
    var ce = window.rsCartEvents;
    if (!fromOpen && tab === 'cart' && !cartWasShown && ce &&
        drawer && drawer.classList.contains('rs-drawer--open')) {
      ce.fetchCart().then(function (cart) {
        if (cart) ce.cartView(drawer, 'dialog', cart);
      });
    }
  };

  // ── Cart footer edge (Option C, 2026-09-28) ───────────────────────────────
  // The footer is position: sticky in the scrolling cart pane (base.css). Add a
  // lifted edge only while list items are hidden under it. Pure read/toggle:
  // no cart writes, no pubsub.
  function rsSyncCartFooterEdge() {
    var pane = document.getElementById('rs-pane-cart');
    var foot = document.getElementById('rs-cart-footer');
    if (!pane || !foot) return;
    var under = pane.scrollHeight - pane.clientHeight - pane.scrollTop > 1;
    foot.classList.toggle('is-overflowing', under && foot.style.display !== 'none');
  }

  // ── Render cart items ───────────────────────────────────────────────────────
  // ── Shopify standard cart events for the drawer (Digital #97) ──────────────
  // Thin wrappers over assets/rs-cart-events.js; each is a no-op when that
  // file or Shopify's standard-events module is missing.
  function rsLinesUpdate(target, action, key, qty) {
    var ce = window.rsCartEvents;
    return ce ? ce.linesUpdate(target, action, 'dialog', [{ id: key, quantity: qty }]) : null;
  }
  // /cart/change.js answers with the whole cart: it resolves the event with
  // no extra read. A 422 answers { status, message, description }.
  function rsSettleLinesUpdate(lines, c) {
    var ce = window.rsCartEvents;
    if (lines) lines.settled = true;
    if (c && c.status) {
      if (lines) lines.reject(new Error(c.description || c.message || 'Cart change failed'));
      if (ce) ce.cartError(document.getElementById('rs-drawer') || document, c.description || c.message, 'INVALID');
      return;
    }
    if (ce) ce.mutated(c);
    if (lines) lines.resolve(c);
  }
  // No event left to settle (StandardEvents missing) or already settled
  // above (a render error after a 422): nothing to send.
  function rsFailLinesUpdate(lines, e) {
    var ce = window.rsCartEvents;
    if (!lines || lines.settled) return;
    lines.reject(e);
    if (ce) ce.cartError(document.getElementById('rs-drawer') || document, (e && e.message) || 'Network error', 'SERVICE_UNAVAILABLE');
  }

  window.rsRenderCart = function (cart) {
    var container   = document.getElementById('rs-cart-items');
    var footer      = document.getElementById('rs-cart-footer');
    var cartCountEl = document.getElementById('rs-cart-count');
    var cartCountS  = document.getElementById('rs-cart-count-s');
    var subtotalEl  = document.getElementById('rs-cart-subtotal');
    var cartBadge   = document.getElementById('rs-badge-cart');
    var headerBadge = document.getElementById('rs-badge');
    if (!container) return;

    var count = cart.item_count || 0;

    // Update tab badge
    if (cartBadge) cartBadge.textContent = count > 0 ? '(' + count + ')' : '';

    // Update header bag badge — cart count only (favourites moved to the
    // dedicated header heart in Commit 6).
    if (headerBadge) {
      headerBadge.dataset.cartCount = count;
      headerBadge.textContent = count > 0 ? count : '';
      headerBadge.style.display = count > 0 ? '' : 'none';
    }

    if (count === 0) {
      container.innerHTML = '<p class="rs-drawer__empty">Your cart is empty.</p>';
      if (footer) footer.style.display = 'none';
      rsSyncCartFooterEdge();
      return;
    }

    // Discount display (backlog #12, 2026-10-02). Same maths as /cart's Order
    // Summary (main-cart-footer.liquid): Subtotal = items_subtotal_price, the
    // sum of the line prices shown (already after line-level discounts, which
    // show on their line as a struck price + title). Order-level discounts get
    // one row each, then Total = total_price. Every string from /cart.js that
    // goes into innerHTML is escaped with rsEsc.
    var labels = container.dataset || {};
    var subtotal = cart.items_subtotal_price != null ? cart.items_subtotal_price : cart.total_price;
    var orderDiscounts = (cart.cart_level_discount_applications || []).filter(function (d) {
      return d && d.total_allocated_amount > 0;
    });
    var orderWrap = document.getElementById('rs-cart-order-discounts');
    var orderRows = document.getElementById('rs-cart-discount-rows');
    var totalEl   = document.getElementById('rs-cart-total');

    if (cartCountEl) cartCountEl.textContent = count;
    if (cartCountS)  cartCountS.textContent  = count !== 1 ? 's' : '';
    if (subtotalEl)  subtotalEl.textContent  = rsMoney(subtotal);
    if (orderWrap && orderRows && totalEl) {
      orderRows.innerHTML = orderDiscounts.map(function (d) {
        return '<div class="rs-cart-footer__discount">' +
            '<span class="rs-body-sm">' + RS_DISCOUNT_ICON +
              '<span class="visually-hidden">' + rsEsc(labels.labelDiscount || 'Discount') + ': </span>' +
              rsEsc(d.title) +
            '</span>' +
            '<span class="rs-body-sm">-' + rsMoney(d.total_allocated_amount) + '</span>' +
          '</div>';
      }).join('');
      totalEl.textContent = rsMoney(cart.total_price);
      orderWrap.hidden = orderDiscounts.length === 0;
    }
    if (footer) footer.style.display = '';

    var html = '';
    cart.items.forEach(function (item) {
      var imgSrc = item.image
        ? item.image.replace(/(\.\w+)(\?|$)/, '_120x120$1$2')
        : '';
      // Alt: the line image's own alt from /cart.js, falling back to the product title
      // (Digital #49 follow-up; same pattern as #52).
      var imgAlt = (item.featured_image && item.featured_image.alt) || item.product_title;
      var price = rsMoney(item.final_line_price);
      var variantLine = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<div class="rs-cart-item__variant">' + rsEsc(item.variant_title) + '</div>'
        : '';
      // Line-level (product-scoped) discount: original struck, then the price
      // paid, plus the discount title(s) under the variant.
      var priceHtml = '<span class="rs-cart-item__price">' + price + '</span>';
      if (item.original_line_price > item.final_line_price) {
        priceHtml =
          '<span class="rs-cart-item__prices">' +
            '<s class="rs-caption rs-cart-item__old-price">' +
              '<span class="visually-hidden">' + rsEsc(labels.labelRegular || 'Regular price') + ' </span>' +
              rsMoney(item.original_line_price) +
            '</s>' +
            '<span class="rs-cart-item__price">' +
              '<span class="visually-hidden">' + rsEsc(labels.labelSale || 'Sale price') + ' </span>' +
              price +
            '</span>' +
          '</span>';
      }
      var lineDiscounts = (item.line_level_discount_allocations || []).filter(function (a) {
        return a && a.amount > 0 && a.discount_application;
      });
      var discountList = lineDiscounts.length
        ? '<ul class="rs-cart-item__discounts" role="list" aria-label="' + rsEsc(labels.labelDiscount || 'Discount') + '">' +
            lineDiscounts.map(function (a) {
              return '<li class="rs-caption rs-cart-item__discount">' + RS_DISCOUNT_ICON +
                rsEsc(a.discount_application.title) + '</li>';
            }).join('') +
          '</ul>'
        : '';
      // Swatch lines are capped at qty 1; suppress the stepper, keep price + Remove.
      var isSwatch = (item.product_type || '').toLowerCase() === 'swatch';
      var stepper = isSwatch ? '' : (
        '<div class="rs-cart-item__stepper">' +
          '<button type="button" class="rs-stepper-btn" data-key="' + item.key + '" data-delta="-1" aria-label="Decrease">\u2212</button>' +
          '<span class="rs-stepper-qty">' + item.quantity + '</span>' +
          '<button type="button" class="rs-stepper-btn" data-key="' + item.key + '" data-delta="1" aria-label="Increase">+</button>' +
        '</div>'
      );
      html +=
        '<div class="rs-cart-item" data-key="' + item.key + '">' +
          (imgSrc ? '<img class="rs-cart-item__img" src="' + imgSrc + '" alt="' + rsEsc(imgAlt) + '">' : '') +
          '<div class="rs-cart-item__details">' +
            '<div class="rs-cart-item__title">' + rsEsc(item.product_title) + '</div>' +
            variantLine +
            discountList +
            '<div class="rs-cart-item__qty-row">' +
              stepper +
              priceHtml +
            '</div>' +
            '<button type="button" class="rs-cart-item__remove" data-key="' + item.key + '" aria-label="Remove">\u00d7 Remove</button>' +
          '</div>' +
        '</div>';
    });
    container.innerHTML = html;
    rsSyncCartFooterEdge();

    // Qty stepper events
    container.querySelectorAll('.rs-stepper-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key   = btn.dataset.key;
        var delta = parseInt(btn.dataset.delta, 10);
        var qtyEl = btn.parentElement.querySelector('.rs-stepper-qty');
        var newQty = Math.max(0, parseInt(qtyEl ? qtyEl.textContent : 1, 10) + delta);
        var lines = rsLinesUpdate(btn, newQty === 0 ? 'remove' : 'update', key, newQty);
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty })
        })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            rsSettleLinesUpdate(lines, c);
            rsRenderCart(c);
            rsUpdateBadge();
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rs-drawer', cartData: c });
          })
          .catch(function (e) { rsFailLinesUpdate(lines, e); });
      });
    });

    // Remove events
    container.querySelectorAll('.rs-cart-item__remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var lines = rsLinesUpdate(btn, 'remove', btn.dataset.key, 0);
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: btn.dataset.key, quantity: 0 })
        })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            rsSettleLinesUpdate(lines, c);
            rsRenderCart(c);
            rsUpdateBadge();
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rs-drawer', cartData: c });
          })
          .catch(function (e) { rsFailLinesUpdate(lines, e); });
      });
    });
  };

  // ── Render favourites ───────────────────────────────────────────────────────
  // Fetches fresh from Swym every call (cheap - Swym caches client-side). The
  // promise gate means the empty state never flickers in for a returning user
  // with a populated wishlist: the loading state shows until truth arrives.
  window.rsRenderFavourites = function () {
    var container = document.getElementById('rs-fav-items');
    if (!container) return;
    rsUpdateBadge();

    if (!window.rsFavourites) {
      container.innerHTML = '<p class="rs-drawer__empty">Loading favourites&hellip;</p>';
      return;
    }

    if (!rsFavourites.isReady) {
      container.innerHTML = '<p class="rs-drawer__empty">Loading favourites&hellip;</p>';
    }

    rsFavourites.list().then(function (items) {
      if (!items || items.length === 0) {
        container.innerHTML = '<p class="rs-drawer__empty">No favourites saved yet.</p>';
        rsUpdateBadge();
        return;
      }

      // Live catalog map (product id -> { url, image, title, price }) emitted
      // by sections/cart-popup.liquid. Resolving each favourite against this
      // beats the frozen Swym snapshot (f.iu / f.du / f.dt / f.pr) when a
      // product is renamed / re-priced / re-imaged. Parsed once per render;
      // guard against a missing or malformed block -> empty map (full snapshot
      // fallback). Single-consumer local lookup — not promoted to a helper.
      var catalog = {};
      var catEl = document.getElementById('rs-fav-catalog');
      if (catEl) {
        try { catalog = JSON.parse(catEl.textContent) || {}; }
        catch (e) { catalog = {}; }
      }

      var html = '';
      items.forEach(function (f) {
        // Found in live catalog -> use live values. Deleted product (not in
        // map) -> fall back to the Swym snapshot fields, current behaviour.
        var live      = catalog[String(f.empi)];
        var titleText = (live && live.title != null ? live.title : f.dt) || '';
        var title     = titleText.replace(/"/g, '&quot;');
        var price     = formatPrice(live && live.price != null ? live.price : f.pr);
        var url       = (live && live.url) || f.du || '#';
        var img       = (live && live.image) || f.iu || '';
        html +=
          '<div class="rs-fav-card" data-empi="' + (f.empi || '') + '" data-epi="' + (f.epi || '') + '">' +
            (img ? '<a href="' + url + '"><img class="rs-fav-card__img" src="' + img + '" alt="' + title + '"></a>' : '') +
            '<div class="rs-fav-card__title"><a href="' + url + '">' + titleText + '</a></div>' +
            '<div class="rs-fav-card__price">' + price + '</div>' +
            '<button type="button" class="rs-fav-card__remove" aria-label="Remove from favourites">\u2665\ufe0e Remove</button>' +
          '</div>';
      });
      container.innerHTML = html;
      rsUpdateBadge();

      container.querySelectorAll('.rs-fav-card__remove').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          var card = btn.closest('.rs-fav-card');
          if (!card) return;
          var data = {
            empi: parseInt(card.dataset.empi, 10),
            epi:  parseInt(card.dataset.epi, 10),
            // Swym's removeFromWishList accepts the variant identity pair;
            // other fields are optional on remove but harmless if present.
            du:   '', dt: '', iu: '', pr: 0
          };
          rsFavourites.remove(data).catch(function (err) {
            // See rsToggleFavourite catch — debug, not warn, post fifth-pass.
            console.debug('Favourites remove (advisory)', err);
          });
        });
      });
    }).catch(function (err) {
      console.warn('rsRenderFavourites: list fetch failed', err);
      container.innerHTML = '<p class="rs-drawer__empty">Couldn\u2019t load favourites. Refresh to retry.</p>';
    });
  };

  // ── Recent-ATC-click tracker (guard for cart-update subscription in init) ──
  // Dawn publishes PUB_SUB_EVENTS.cartUpdate on every cart-state change —
  // including programmatic ones from the /cart page and quick-order list.
  // Guard prevents the drawer from auto-opening on non-ATC cart mutations.
  var _lastATCClick = 0;
  document.addEventListener('click', function (e) {
    if (e.target.closest('.product-form__submit, [name="add"], [data-add-to-cart]')) {
      _lastATCClick = Date.now();
    }
  }, true);

  // ── Init on DOMContentLoaded ────────────────────────────────────────────────
  function init() {
    // Seed header badge from current cart
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var headerBadge = document.getElementById('rs-badge');
        if (headerBadge) headerBadge.dataset.cartCount = cart.item_count || 0;
        rsUpdateBadge();
      })
      .catch(function () { rsUpdateBadge(); });

    var cartPane = document.getElementById('rs-pane-cart');
    if (cartPane) cartPane.addEventListener('scroll', rsSyncCartFooterEdge, { passive: true });
    // Opening or closing the drawer gift panel (<details>) changes the list
    // height without a scroll. toggle does not bubble, so listen in capture.
    if (cartPane) cartPane.addEventListener('toggle', rsSyncCartFooterEdge, true);
    window.addEventListener('resize', rsSyncCartFooterEdge);

    initHeartButtons();
    _heartObserver.observe(document.body, { childList: true, subtree: true });

    // Wishlist truth lives in Swym now. Any add/remove (from this tab,
    // another tab, or a Swym-driven action) emits onChange — repaint hearts
    // and the drawer Favourites pane if it's currently visible.
    if (window.rsFavourites) {
      rsFavourites.onChange(function () {
        rsUpdateHeartButtons();
        rsUpdateBadge();
        var drawer = document.getElementById('rs-drawer');
        var favPane = document.getElementById('rs-pane-fav');
        if (drawer && drawer.classList.contains('rs-drawer--open') &&
            favPane && !favPane.classList.contains('rs-drawer__pane--hidden')) {
          rsRenderFavourites();
        }
      });
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
      // Ignore our own publish — drawer qty/remove fires cartUpdate so PDP
      // surfaces (swatch CTAs, etc.) can react, but it must not re-open the
      // drawer over the user's own interaction inside it. Quick-add (YMAL on
      // /cart) renders in place, so it must not auto-open the drawer either.
      if (evt && (evt.source === 'rs-drawer' || evt.source === 'rs-quick-add')) return;
      if (Date.now() - _lastATCClick < 30000) {
        rsOpenDrawer('cart');
      }
    });

    // Separate job from Path A above: keep the header bag badge in sync after
    // in-page cart mutations. Native qty publishes source 'cart-items';
    // quick-add publishes 'rs-quick-add'. Drawer ('rs-drawer') already repaints
    // the badge itself, so skip it.
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (evt) {
      if (!evt || evt.source === 'rs-drawer') return;
      var count = evt.cartData && typeof evt.cartData.item_count === 'number' ? evt.cartData.item_count : null;
      if (count === null) return;
      var badge = document.getElementById('rs-badge');
      if (badge) badge.dataset.cartCount = count;
      if (typeof window.rsUpdateBadge === 'function') window.rsUpdateBadge();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
